import { useState, useEffect, useMemo } from "react";

import {
  CreateTableNew as CreateTable,
  CreateTableWithSelection,
  SearchBar,
} from '../../../../common/APICommon';
import { ConfirmationModal } from '../../../../common/Modal';

import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from '@mui/icons-material/DeleteForever';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';

// Participants Tab Component with custom cell rendering
export function ParticipantsTab({
  form,
  employeesList,
  loadingEmployees,
  handleSelectEmployee,
  handleRemoveParticipant,
  handleSelectMultiple,
  handleParticipantStatusChange
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [participantSearchTerm, setParticipantSearchTerm] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState({
    title: "",
    message: "",
    type: "info",
    action: null,
    isLoading: false
  });
  const itemsPerPage = 10;

  // Define headers and accessors
  const participantsHeaders = [
    "Employee Id",
    "Full Name",
    "Department",
    "Position",
    "Status",
    "Actions"
  ];

  const participantsAccessors = [
    "employeeId",
    "fullName",
    "department",
    "position",
    "status",
    "actions"
  ];

  // Filter participants based on search term
  const filteredParticipants = useMemo(() => {
    if (!participantSearchTerm || !form.participants) return form.participants || [];

    const searchLower = participantSearchTerm.toLowerCase();
    return form.participants.filter(participant => {
      return (
        participant.employeeId?.toString().includes(participantSearchTerm) ||
        participant.fullName?.toLowerCase().includes(searchLower) ||
        participant.department?.toLowerCase().includes(searchLower) ||
        participant.position?.toLowerCase().includes(searchLower)
      );
    });
  }, [form.participants, participantSearchTerm]);

  // Define actions for participants table
  const participantActions = [
    {
      name: "Remove",
      tooltip: "remove participant",
      icon: <DeleteIcon fontSize="small" />,
      className: "bg-red-500 hover:bg-red-600 flex items-center",
      function: (participant) => showDeleteConfirmation(participant),
      visible: true,
    }
  ];

  // Show confirmation for single participant deletion
  const showDeleteConfirmation = (participant) => {
    setConfirmationConfig({
      title: "Remove Participant",
      message: `Are you sure you want to remove "${participant.fullName}" from this Training/Seminar Program?`,
      type: "danger",
      action: () => {
        handleRemoveParticipant(participant.employeeId);
        setConfirmationConfig(prev => ({ ...prev, isLoading: false }));
      },
      isLoading: false
    });
    setShowConfirmationModal(true);
  };

  // Show confirmation for bulk status update
  const showBulkStatusConfirmation = (status) => {
    const statusText = {
      pending: "Pending",
      attended: "Attended",
      not_attended: "Not Attended"
    }[status];

    const participants = filteredParticipants.length > 0 ? filteredParticipants : form.participants;
    const count = participants?.length || 0;

    setConfirmationConfig({
      title: `Set Status to ${statusText}`,
      message: `Are you sure you want to set status to "${statusText}" for ${count} participant${count !== 1 ? 's' : ''}?`,
      type: "warning",
      action: () => {
        // Update all filtered participants
        if (filteredParticipants.length > 0) {
          filteredParticipants.forEach(participant => {
            handleParticipantStatusChange(participant.employeeId, status);
          });
        } else if (form.participants && form.participants.length > 0) {
          // If no search filter, update all participants
          form.participants.forEach(participant => {
            handleParticipantStatusChange(participant.employeeId, status);
          });
        }
        setConfirmationConfig(prev => ({ ...prev, isLoading: false }));
        setShowOptionsMenu(false);
      },
      isLoading: false
    });
    setShowConfirmationModal(true);
  };

  // Show confirmation for clearing all participants
  const showClearAllConfirmation = () => {
    const participants = filteredParticipants.length > 0 ? filteredParticipants : form.participants;
    const count = participants?.length || 0;

    setConfirmationConfig({
      title: "Clear All Participants",
      message: `Are you sure you want to remove all ${count} participant${count !== 1 ? 's' : ''}? This action cannot be undone.`,
      type: "danger",
      action: () => {
        if (filteredParticipants.length > 0) {
          // Remove all filtered participants
          filteredParticipants.forEach(participant => {
            handleRemoveParticipant(participant.employeeId);
          });
        } else if (form.participants && form.participants.length > 0) {
          // If no search filter, remove all participants
          form.participants.forEach(participant => {
            handleRemoveParticipant(participant.employeeId);
          });
        }
        setConfirmationConfig(prev => ({ ...prev, isLoading: false }));
        setShowOptionsMenu(false);
      },
      isLoading: false
    });
    setShowConfirmationModal(true);
  };

  // Handle confirmation
  const handleConfirm = () => {
    if (confirmationConfig.action) {
      setConfirmationConfig(prev => ({ ...prev, isLoading: true }));
      confirmationConfig.action();
      setShowConfirmationModal(false);
    }
  };

  // Close confirmation modal
  const closeConfirmationModal = () => {
    setShowConfirmationModal(false);
    setConfirmationConfig({
      title: "",
      message: "",
      type: "info",
      action: null,
      isLoading: false
    });
  };

  // Custom cell renderer for status dropdown
  const customCellRenderer = (row, accessor) => {
    if (accessor === "status") {
      const status = row.status || 'pending';

      // Define color schemes for each status (only 3 options now)
      const statusConfig = {
        pending: {
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          borderColor: 'border-yellow-300',
          hoverBg: 'hover:bg-yellow-200',
          dotColor: 'bg-yellow-500',
          focusColor: 'focus:ring-yellow-400'
        },
        attended: {
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          borderColor: 'border-green-300',
          hoverBg: 'hover:bg-green-200',
          dotColor: 'bg-green-500',
          focusColor: 'focus:ring-green-400'
        },
        not_attended: {
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          borderColor: 'border-red-300',
          hoverBg: 'hover:bg-red-200',
          dotColor: 'bg-red-500',
          focusColor: 'focus:ring-red-400'
        }
      };

      const config = statusConfig[status] || statusConfig.pending;

      return (
        <div className="relative group">
          {/* Custom styled dropdown */}
          <select
            value={status}
            onChange={(e) => handleParticipantStatusChange(row.employeeId, e.target.value)}
            className={`
            w-full min-w-[140px] 
            ${config.bgColor} ${config.textColor} ${config.borderColor}
            border rounded-lg pl-8 pr-8 py-1.5
            text-sm font-medium
            appearance-none cursor-pointer
            transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-opacity-50 
            ${config.focusColor}
            hover:shadow-sm
          `}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="pending" className="bg-white text-gray-800">Pending</option>
            <option value="attended" className="bg-white text-gray-800">Attended</option>
            <option value="not_attended" className="bg-white text-gray-800">Not Attended</option>
          </select>

          {/* Custom dropdown arrow */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-1 pointer-events-none">
            <svg
              className={`w-4 h-4 ${config.textColor}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Status indicator dot */}
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <div className={`w-2 h-2 rounded-full ${config.dotColor}`}></div>
          </div>
        </div>
      );
    }

    if (accessor === "employeeId") {
      const value = row[accessor] ?? "-";
      return (
        <div className="flex items-center">
          <div className="bg-gray-100 text-gray-600 px-3 py-1.5 rounded text-xs font-mono">
            #{value}
          </div>
        </div>
      );
    }

    if (accessor === "department" || accessor === "position") {
      const value = row[accessor] ?? "-";
      return (
        <div className="flex items-center">
          <div className="bg-gray-50 text-gray-700 px-3 py-1.5 rounded-full text-xs border border-gray-200">
            {value}
          </div>
        </div>
      );
    }

    // Default rendering for other cells
    const value = row[accessor] ?? "-";
    const isTrue = ["True", "Yes", "Subscribed", "Active", "Completed"].includes(value);
    const isFalse = ["False", "No", "Unsubscribed", "Inactive", "Unapproved"].includes(value);

    if (isTrue) {
      return (
        <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-green-100 text-green-800 text-sm font-medium">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-2"></div>
          {value}
        </div>
      );
    }

    if (isFalse) {
      return (
        <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-red-100 text-red-800 text-sm font-medium">
          <div className="w-1.5 h-1.5 rounded-full bg-red-500 mr-2"></div>
          {value}
        </div>
      );
    }

    return (
      <span className="text-gray-700 px-2">
        {value}
      </span>
    );
  };

  const handleAddSelectedEmployees = () => {
    if (selectedEmployees.length > 0) {
      handleSelectMultiple(selectedEmployees);
      setSelectedEmployees([]);
      setShowEmployeeModal(false);
    }
  };

  // Close options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOptionsMenu && !event.target.closest('.options-menu')) {
        setShowOptionsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOptionsMenu]);

  return (
    <div className="space-y-4">
      {/* Participants Summary */}
      <div className="rounded-lg border border-gray-300 bg-white p-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h3 className="font-medium text-gray-700">Selected Participants ({form.participants?.length || 0})</h3>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search bar for participants */}
            {form.participants && form.participants.length > 0 && (
              <div className="w-full sm:w-auto">
                <SearchBar
                  handleFilter={setParticipantSearchTerm}
                  placeholder="Search participants..."
                />
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center space-x-2">
              {/* Options button with dropdown */}
              {form.participants && form.participants.length > 0 && (
                <div className="relative options-menu">
                  <button
                    onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                    className="flex items-center space-x-2 rounded border-none px-4 py-2 text-white bg-blue-500 hover:bg-blue-600 hover:border-gray-400"
                  >
                    <SettingsIcon fontSize="small" />
                    <span>Options</span>
                    <svg
                      className={`w-4 h-4 transition-transform ${showOptionsMenu ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Options dropdown menu */}
                  {showOptionsMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                      <div className="p-3 border-b border-gray-100">
                        <h4 className="font-medium text-gray-700 text-sm">Bulk Actions</h4>
                        <p className="text-xs text-gray-500 mt-1">
                          Apply to {filteredParticipants.length > 0 ?
                            `${filteredParticipants.length} filtered participants` :
                            `${form.participants?.length || 0} all participants`}
                        </p>
                      </div>

                      <div className="p-2">
                        {/* Bulk status updates */}
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 font-medium mb-2">SET STATUS TO:</p>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => showBulkStatusConfirmation('pending')}
                              className="flex items-center space-x-2 px-3 py-2 text-sm bg-yellow-50 text-yellow-700 rounded hover:bg-yellow-100 border border-yellow-200"
                            >
                              <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                              <span>Pending</span>
                            </button>
                            <button
                              onClick={() => showBulkStatusConfirmation('attended')}
                              className="flex items-center space-x-2 px-3 py-2 text-sm bg-green-50 text-green-700 rounded hover:bg-green-100 border border-green-200"
                            >
                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              <span>Attended</span>
                            </button>
                            <button
                              onClick={() => showBulkStatusConfirmation('not_attended')}
                              className="flex items-center space-x-2 px-3 py-2 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100 border border-red-200"
                            >
                              <div className="w-2 h-2 rounded-full bg-red-500"></div>
                              <span>Not Attended</span>
                            </button>
                          </div>
                        </div>

                        {/* Clear all button */}
                        <button
                          onClick={showClearAllConfirmation}
                          className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-sm bg-red-500 text-white rounded hover:bg-red-600 border-none mt-2"
                        >
                          <DeleteIcon fontSize="small" />
                          <span>Clear All Participants</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Add Employee button */}
              <button
                onClick={() => setShowEmployeeModal(true)}
                className="flex items-center justify-center space-x-2 rounded border-none bg-green-500 px-4 py-2 text-white hover:bg-green-600 whitespace-nowrap"
              >
                <PersonAddIcon fontSize="small" />
                <span>Add Employee(s)</span>
              </button>
            </div>
          </div>
        </div>

        {form.participants && form.participants.length > 0 ? (
          <div className="mt-4">
            <CreateTable
              tableName={"Participants"}
              headers={participantsHeaders}
              accessors={participantsAccessors}
              data={filteredParticipants}
              emptyMessage={participantSearchTerm ? `No participants found matching "${participantSearchTerm}"` : "No participants"}
              actions={participantActions}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredParticipants.length}
              customCellRenderer={customCellRenderer}
            />
          </div>
        ) : (
          <div className="mt-4 text-center py-8 text-gray-500">
            <GroupIcon className="mx-auto mb-3 text-4xl text-gray-300" />
            <p>No participants added yet.</p>
            <p className="text-sm mt-1">Click "Add Employee(s)" to select participants for this program.</p>
          </div>
        )}
      </div>

      {/* Employee Selection Modal */}
      {showEmployeeModal && (
        <EmployeeSelectionModal
          employees={employeesList}
          loading={loadingEmployees}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedEmployees={selectedEmployees}
          setSelectedEmployees={setSelectedEmployees}
          handleAddSelectedEmployees={handleAddSelectedEmployees}
          onClose={() => {
            setShowEmployeeModal(false);
            setSelectedEmployees([]);
            setSearchTerm(""); // Clear search when closing
          }}
          existingParticipants={form.participants}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={showConfirmationModal}
        onClose={closeConfirmationModal}
        onConfirm={handleConfirm}
        title={confirmationConfig.title}
        message={confirmationConfig.message}
        confirmText="Confirm"
        cancelText="Cancel"
        type={confirmationConfig.type}
        isLoading={confirmationConfig.isLoading}
      />
    </div>
  );
}

// Employee Selection Modal Component
function EmployeeSelectionModal({
  employees,
  loading,
  searchTerm,
  setSearchTerm,
  selectedEmployees,
  setSelectedEmployees,
  handleAddSelectedEmployees,
  onClose,
  existingParticipants
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const itemsPerPage = 10;

  // Define table headers and accessors
  const headers = ["Employee Id", "Full Name", "Department", "Position", "Status"];
  const accessors = ["employeeId", "fullName", "department", "position", "status"];

  // Filter employees when search term changes
  useEffect(() => {
    if (!searchTerm) {
      setFilteredEmployees(employees);
      return;
    }

    const lowercaseValue = searchTerm.toLowerCase();
    const filtered = employees.filter(employee => {
      return (
        employee.fullName?.toLowerCase().includes(lowercaseValue) ||
        employee.id?.toString().includes(searchTerm) ||
        employee.employeeInformation?.departmentDescription?.toLowerCase().includes(lowercaseValue) ||
        employee.employeeInformation?.positionDescription?.toLowerCase().includes(lowercaseValue)
      );
    });

    setFilteredEmployees(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [searchTerm, employees]);

  // Check if employee is already added
  const isEmployeeDisabled = (employee) => {
    return existingParticipants.some(p => p.employeeId === employee.employeeId);
  };

  // Handle individual selection
  const handleSelectItem = (employee) => {
    if (isEmployeeDisabled(employee)) return;

    setSelectedEmployees(prev => {
      const isSelected = prev.some(e => e.id === employee.id);
      if (isSelected) {
        return prev.filter(e => e.id !== employee.id);
      } else {
        return [...prev, employee];
      }
    });
  };

  // Handle select all
  const handleSelectAll = (newSelection) => {
    setSelectedEmployees(newSelection);
  };

  // Format employee data for the table
  const formattedEmployees = useMemo(() => {
    return filteredEmployees.map(employee => ({
      ...employee,
      employeeId: employee.employeeId,
      department: employee.employeeInformation?.departmentDescription || "-",
      position: employee.employeeInformation?.positionDescription || "-",
      status: employee.status || 'Active'
    }));
  }, [filteredEmployees]);

  // Filter out already added employees for the "select all" functionality
  const selectableEmployees = useMemo(() => {
    return formattedEmployees.filter(emp => !isEmployeeDisabled(emp));
  }, [formattedEmployees, isEmployeeDisabled]);

  // Handle search filter
  const handleSearchFilter = (value) => {
    setSearchTerm(value);
  };

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-3">
      <div className="relative flex h-auto max-h-[80vh] w-full max-w-6xl flex-col rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-2 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800">Select Employees</h2>
          <button
            onClick={onClose}
            className="border-none bg-transparent p-1 text-gray-400 hover:text-gray-700"
          >
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4">
            <SearchBar handleFilter={handleSearchFilter} />
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
              <p className="mt-2 text-gray-600">Loading employees...</p>
            </div>
          ) : (
            <CreateTableWithSelection
              tableName="Employees"
              headers={headers}
              accessors={accessors}
              data={formattedEmployees}
              emptyMessage="No employees found matching your search"
              selectedItems={selectedEmployees}
              onSelectItem={handleSelectItem}
              onSelectAll={handleSelectAll}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={formattedEmployees.length}
              selectionDisabledChecker={isEmployeeDisabled}
              selectionDisabledMessage="Already added"
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between rounded-b-lg border-t border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{selectedEmployees.length}</span> employee(s) selected
            {selectableEmployees.length > 0 && (
              <span className="ml-2">
                ({selectableEmployees.length} available to select)
              </span>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={onClose}
              className="rounded border-none bg-gray-400 px-4 py-2 text-white hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={handleAddSelectedEmployees}
              disabled={selectedEmployees.length === 0}
              className="rounded border-none bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Add Selected ({selectedEmployees.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}